import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatSessionTime } from '../game/model/session'
import { useGameStore } from '../store/gameStore'
import { DIFFICULTY_CATALOG } from '../game/combat/difficulty/catalog'
import type { DifficultyId } from '../game/combat/types/primitives'

function nextSpeed(current: number): 1 | 2 | 3 {
  if (current >= 3) return 1
  if (current >= 2) return 3
  return 2
}

function colorCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`
}

function campaignMatchLabel(
  campaign: { displayLabel: string; difficultyId: string } | null | undefined
): string | null {
  if (!campaign) return null
  const def = DIFFICULTY_CATALOG[campaign.difficultyId as DifficultyId]
  const diff = (def?.displayName ?? campaign.difficultyId).toUpperCase()
  return `${campaign.displayLabel} · ${diff}`
}

/** Match-only HUD. Reads GameSession mirror. Not persistent currency. */
export const MatchBar: React.FC = () => {
  const navigate = useNavigate()
  const { sessionView, pauseGame, setGameSpeed, startWave, playtestMap, endPlaytest } =
    useGameStore()
  const [previewOpen, setPreviewOpen] = useState(false)

  if (!sessionView) return null
  if (sessionView.state === 'VICTORY' || sessionView.state === 'DEFEAT') return null

  const speed = sessionView.simulationSpeed
  const waveCompact = `${sessionView.waveNumber}/${sessionView.waveCount}`
  const canStart = sessionView.canStartWave
  const prepRemaining = sessionView.prepCountdownRemaining
  const prepBonus = sessionView.earlyStartBonusPreview
  const isPlaytest = playtestMap !== null
  const preview = sessionView.wavePreview
  const progress = sessionView.waveProgress
  const isReady = sessionView.state === 'READY'
  const isPlaying = sessionView.state === 'PLAYING'
  const matchId = campaignMatchLabel(sessionView.campaign)

  return (
    <div className="match-bar" role="status">
      {matchId ? (
        <p className="match-bar__identity" data-testid="match-identity">
          {matchId}
        </p>
      ) : null}
      <div className="match-bar__hud">
        <div className="match-bar__stats match-bar__stats--primary">
          <span className="match-stat match-stat--primary" title="Lives">
            <span className="match-stat__icon match-stat__icon--lives" aria-hidden>
              ♥
            </span>
            <span className="match-stat__value match-stat__value--lives">{sessionView.lives}</span>
            <span className="match-stat__label">Lives</span>
          </span>
          <span className="match-stat match-stat--primary" title="Coins">
            <span className="match-stat__icon match-stat__icon--gold" aria-hidden>
              ◆
            </span>
            <span className="match-stat__value match-stat__value--gold">{sessionView.gold}</span>
            <span className="match-stat__label">Coins</span>
          </span>
          <button
            type="button"
            className="match-stat match-stat--primary match-stat--wave"
            title={sessionView.waveLabel}
            aria-expanded={previewOpen}
            onClick={() => setPreviewOpen((open) => !open)}
          >
            <span className="match-stat__icon match-stat__icon--wave" aria-hidden>
              W
            </span>
            <span className="match-stat__value match-stat__value--wave-full match-stat__value--wave">
              {sessionView.waveLabel}
            </span>
            <span className="match-stat__value match-stat__value--wave-compact match-stat__value--wave">
              {waveCompact}
            </span>
            <span className="match-stat__label">Wave</span>
          </button>
        </div>
        <div className="match-bar__stats match-bar__stats--secondary">
          <span className="match-stat match-stat--secondary" title="Time">
            <span className="match-stat__icon match-stat__icon--time" aria-hidden>
              ⏱
            </span>
            <span className="match-stat__value match-stat__value--time">
              {formatSessionTime(sessionView.elapsedTime)}
            </span>
            <span className="match-stat__label">Time</span>
          </span>
          <span className="match-stat match-stat--secondary" title="Defeated">
            <span className="match-stat__icon match-stat__icon--defeated" aria-hidden>
              ★
            </span>
            <span className="match-stat__value match-stat__value--defeated">{sessionView.defeatedEnemies}</span>
            <span className="match-stat__label">Defeated</span>
          </span>
        </div>
      </div>

      {(isReady || previewOpen || isPlaying) && preview && (
        <div className={`wave-intel ${isPlaying ? 'wave-intel--playing' : ''}`}>
          {isReady && (
            <>
              <div className="wave-intel__row">
                <span className="wave-intel__title">
                  {preview.totalEnemies} enemies · Reward {preview.reward}
                </span>
                {sessionView.lastWaveReward > 0 && (
                  <span className="wave-intel__clear">Clear +{sessionView.lastWaveReward}</span>
                )}
              </div>
              <div className="wave-intel__chips" aria-label="Upcoming enemies">
                {preview.chips.map((chip) => {
                  const rankSuffix =
                    chip.enemyRank === 'ELITE'
                      ? ' ELITE'
                      : chip.enemyRank === 'BOSS'
                        ? ' BOSS'
                        : ''
                  const laneSuffix = chip.laneLabel ? ` · ${chip.laneLabel}` : ''
                  const label = `${chip.name.toUpperCase()} Lv${chip.enemyLevel}${rankSuffix} ×${chip.count}${laneSuffix}`
                  const title = `${label}${chip.movementClass === 'AIR' && !chip.laneLabel ? ' (AIR)' : ''}`
                  return (
                    <span
                      key={chip.chipKey}
                      className={`wave-chip${chip.movementClass === 'AIR' ? ' wave-chip--air' : ''}${chip.laneLabel && chip.laneLabel !== 'AIR' ? ' wave-chip--lane' : ''}`}
                      title={title}
                      aria-label={title}
                      data-lane={chip.laneLabel}
                    >
                      <span
                        className="wave-chip__dot"
                        style={{ background: colorCss(chip.color) }}
                        aria-hidden
                      />
                      {chip.movementClass === 'AIR' && (
                        <span className="wave-chip__air" aria-hidden>
                          AIR
                        </span>
                      )}
                      {chip.laneLabel && chip.laneLabel !== 'AIR' && (
                        <span className="wave-chip__lane" aria-hidden>
                          {chip.laneLabel}
                        </span>
                      )}
                      <span className="wave-chip__label">{label}</span>
                    </span>
                  )
                })}
              </div>
            </>
          )}
          {isPlaying && progress && (
            <div className="wave-intel__row wave-intel__row--progress">
              <span className="wave-intel__title">{progress.unresolved} left</span>
              <span className="wave-intel__meta">
                {progress.alive} alive · {progress.spawned}/{progress.total} spawned
              </span>
            </div>
          )}
        </div>
      )}

      <div className="match-bar__controls">
        {isPlaytest && (
          <button
            type="button"
            className="match-control"
            data-testid="exit-playtest"
            title="Return to map editor"
            onClick={() => {
              endPlaytest()
              navigate('/map-editor')
            }}
          >
            EXIT
          </button>
        )}
        {sessionView.isPrepCountdown && prepRemaining > 0 && (
          <span className="match-prep" data-testid="wave-prep-countdown" aria-live="polite">
            <span className="match-prep__label">NEXT IN</span>
            <span className="match-prep__value">{prepRemaining}</span>
          </span>
        )}
        {canStart && (
          <button
            type="button"
            className="match-control match-control--start"
            title={
              prepBonus > 0
                ? `Launch now for +${prepBonus} coins`
                : 'Start wave now'
            }
            aria-label={
              prepBonus > 0 ? `Start now, bonus ${prepBonus} coins` : 'Start wave now'
            }
            data-testid="start-wave"
            data-tutorial-target="start"
            onClick={(event) => {
              event.preventDefault()
              startWave()
            }}
          >
            <span className="match-control__start-full">START NOW</span>
            <span className="match-control__start-compact">START NOW</span>
            {prepBonus > 0 ? (
              <span className="match-control__start-bonus" data-testid="early-start-bonus">
                +{prepBonus}
              </span>
            ) : null}
          </button>
        )}
        <div className="match-speed match-speed--desktop" role="group" aria-label="Simulation speed">
          {([1, 2, 3] as const).map((value) => (
            <button
              key={value}
              type="button"
              onPointerUp={(event) => {
                event.preventDefault()
                setGameSpeed(value)
              }}
              className={`match-control match-control--speed ${speed === value ? 'match-control--active' : ''}`}
            >
              {value}x
            </button>
          ))}
        </div>
        <button
          type="button"
          className="match-control match-control--speed-cycle"
          title="Cycle speed"
          aria-label={`Simulation speed ${speed}x, tap to change`}
          onPointerUp={(event) => {
            event.preventDefault()
            setGameSpeed(nextSpeed(speed))
          }}
        >
          {speed}x
        </button>
        <button
          type="button"
          onPointerUp={(event) => {
            event.preventDefault()
            pauseGame()
          }}
          className="match-control match-control--pause"
          title="Pause (ESC)"
          aria-label="Pause"
        >
          <span className="match-control__pause-label">Pause</span>
          <span className="match-control__pause-icon" aria-hidden>
            ❚❚
          </span>
        </button>
      </div>
    </div>
  )
}
